import { GoalCard } from "./GoalCard";
import { GoalInsights } from "./GoalInsights";
import { GoalCreationDialog } from "./GoalCreationDialog";
import { useGoals } from "@/hooks/useGoals";
import { Loader2, Target } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";

export const GoalsOverview = () => {
  const { goals, loading, fetchGoals } = useGoals();

  const mappedGoals = goals.map((g) => ({
    id: g.id,
    title: g.title,
    target: g.target_value,
    current: g.current_value,
    type: "increase" as const,
    metric: g.unit === "percentage" ? "%" : g.unit === "currency" ? "USD" : g.unit,
    timeframe: g.target_date
      ? new Date(g.target_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
      : g.category,
  }));

  const insights = goals.length > 0
    ? goals
        .filter((g) => g.status === "active")
        .slice(0, 3)
        .map((g) => {
          const progress = g.target_value > 0 ? (g.current_value / g.target_value) * 100 : 0;
          if (progress >= 80) return { type: "success" as const, message: `"${g.title}" is ${Math.round(progress)}% complete — almost there!` };
          if (progress < 30) return { type: "warning" as const, message: `"${g.title}" is only ${Math.round(progress)}% complete — needs attention` };
          return { type: "info" as const, message: `"${g.title}" is ${Math.round(progress)}% towards target` };
        })
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Business Goals</h2>
          <p className="text-muted-foreground">Track and manage your business objectives</p>
        </div>
        <GoalCreationDialog onGoalCreated={fetchGoals} />
      </div>

      {mappedGoals.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No goals yet"
          description="Create your first business goal to start tracking progress"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mappedGoals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} />
          ))}
        </div>
      )}

      {insights.length > 0 && <GoalInsights insights={insights} />}
    </div>
  );
};
